import type { SupportTicketSort } from "@redotech/effect-api-shared/schema/query-options";
import {
  getSupportTicketLeafFilters,
  SupportTicketFilterNames,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import { composeWrappers } from "@redotech/react-util/component";
import type { ArrayStringFilterData } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { FilterOperator } from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import type {
  AdvancedFilterData,
  FilterGroup,
} from "@redotech/redo-model/views/views";
import { isSpamTagExclusionFilter } from "@redotech/redo-support-v2/query/default-views";
import {
  buildSearchWithSupportTicketQueryOptions,
  DEFAULT_FILTER_GROUP,
  DEFAULT_QUERY_OPTIONS,
  encodeSupportTicketQueryOptionsForRequest,
  parseSupportTicketQueryOptionsFromSearch,
  supportTicketQueryOptionsAreEqual,
  type SupportTicketQueryOptions,
} from "@redotech/redo-support-v2/query/support-ticket-query-options";
import {
  createContext,
  Dispatch,
  memo,
  ReactElement,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

const DEFAULT_STATUS_FILTER = DEFAULT_FILTER_GROUP
  .clauses[0] as ArrayStringFilterData;

export const QueryOptionsContext = createContext<SupportTicketQueryOptions>(
  DEFAULT_QUERY_OPTIONS,
);

export const SetQueryOptionsContext = createContext<
  Dispatch<SetStateAction<SupportTicketQueryOptions>>
>(() => {});

export const SupportTicketFiltersProvider = memo(
  function SupportTicketFiltersProvider({
    children,
  }: {
    children: ReactElement;
  }) {
    const navigate = useNavigate();
    const location = useLocation();

    const [queryOptions, setQueryOptionsState] =
      useState<SupportTicketQueryOptions>(() =>
        parseSupportTicketQueryOptionsFromSearch(location.search),
      );

    // When the URL changes externally (back/forward navigation, sidebar link),
    // reset state to match. We compare the parsed filter groups rather than
    // the encoded URL strings, since encode/decode is not idempotent.
    useEffect(() => {
      const urlQueryOptions = parseSupportTicketQueryOptionsFromSearch(
        location.search,
      );
      setQueryOptionsState((currentQueryOptions) =>
        supportTicketQueryOptionsAreEqual(urlQueryOptions, currentQueryOptions)
          ? currentQueryOptions
          : urlQueryOptions,
      );
    }, [location.search]);

    const setQueryOptions: Dispatch<SetStateAction<SupportTicketQueryOptions>> =
      useCallback(
        (action) => {
          setQueryOptionsState((prev) => {
            const newOptions =
              typeof action === "function" ? action(prev) : action;
            const newSearch = buildSearchWithSupportTicketQueryOptions(
              location.search,
              newOptions,
            );
            navigate({ search: newSearch }, { replace: true });
            return newOptions;
          });
        },
        [location.search, navigate],
      );

    return composeWrappers(
      (inner) => (
        <QueryOptionsContext.Provider value={queryOptions}>
          {inner}
        </QueryOptionsContext.Provider>
      ),
      (inner) => (
        <SetQueryOptionsContext.Provider value={setQueryOptions}>
          {inner}
        </SetQueryOptionsContext.Provider>
      ),
    )(children);
  },
);

export function useQueryOptions(): SupportTicketQueryOptions {
  return useContext(QueryOptionsContext);
}

export function useEncodedSupportTicketFilterGroup(): string {
  const queryOptions = useContext(QueryOptionsContext);
  return useMemo(
    () => encodeSupportTicketQueryOptionsForRequest(queryOptions),
    [queryOptions],
  );
}

export function useQueryOptionsState(): {
  queryOptions: SupportTicketQueryOptions;
  setQueryOptions: Dispatch<SetStateAction<SupportTicketQueryOptions>>;
} {
  const queryOptions = useContext(QueryOptionsContext);
  const setQueryOptions = useContext(SetQueryOptionsContext);
  return { queryOptions, setQueryOptions };
}

export function useResetFilters(): () => void {
  const setQueryOptions = useContext(SetQueryOptionsContext);

  return useCallback(() => {
    setQueryOptions(DEFAULT_QUERY_OPTIONS);
  }, [setQueryOptions]);
}

export function useHasActiveFilters(): boolean {
  const queryOptions = useContext(QueryOptionsContext);

  return useMemo(
    () =>
      !supportTicketQueryOptionsAreEqual(queryOptions, DEFAULT_QUERY_OPTIONS),
    [queryOptions],
  );
}

export function useStatusFilter(): {
  statuses: string[];
  setStatuses: (statuses: string[]) => void;
} {
  const { queryOptions, setQueryOptions } = useQueryOptionsState();

  const statusFilter = useMemo(() => {
    const filters = getSupportTicketLeafFilters(queryOptions.filterGroup);
    return filters.find(
      (f): f is ArrayStringFilterData =>
        f.name === SupportTicketFilterNames.STATUS,
    );
  }, [queryOptions.filterGroup]);

  const statuses = useMemo(() => {
    if (!statusFilter || !statusFilter.value) {
      return [];
    }
    return statusFilter.value;
  }, [statusFilter]);

  const setStatuses = useCallback(
    (newStatuses: string[]) => {
      setQueryOptions((prev) => {
        const otherFilters = getSupportTicketLeafFilters(
          prev.filterGroup,
        ).filter((f) => f.name !== SupportTicketFilterNames.STATUS);
        if (newStatuses.length === 0) {
          return {
            ...prev,
            filterGroup: {
              operator: FilterOperator.AND,
              clauses: otherFilters,
            },
          };
        }
        const newFilter: ArrayStringFilterData = {
          ...DEFAULT_STATUS_FILTER,
          value: newStatuses,
        };
        return {
          ...prev,
          filterGroup: {
            operator: FilterOperator.AND,
            clauses: [...otherFilters, newFilter],
          },
        };
      });
    },
    [setQueryOptions],
  );

  return { statuses, setStatuses };
}

export function useColumnSort(sortKind: SupportTicketSort["kind"]): {
  sortDirection: "asc" | "desc" | null;
  toggleSort: () => void;
} {
  const { queryOptions, setQueryOptions } = useQueryOptionsState();

  const sortDirection = useMemo((): "asc" | "desc" | null => {
    const sort = queryOptions.sorts.find((s) => s.kind === sortKind);
    return sort?.direction ?? null;
  }, [queryOptions.sorts, sortKind]);

  const toggleSort = useCallback(() => {
    setQueryOptions((prev) => {
      const otherSorts = prev.sorts.filter((s) => s.kind !== sortKind);

      let newSorts: SupportTicketSort[];
      switch (sortDirection) {
        case null:
          newSorts = [
            { kind: sortKind, direction: "desc", nulls: "last" },
            ...otherSorts,
          ];
          break;
        case "desc":
          newSorts = [
            { kind: sortKind, direction: "asc", nulls: "last" },
            ...otherSorts,
          ];
          break;
        case "asc":
          newSorts = otherSorts;
          break;
      }

      return { ...prev, sorts: newSorts };
    });
  }, [setQueryOptions, sortKind, sortDirection]);

  return { sortDirection, toggleSort };
}

export function useAddFilter(): (filter: AdvancedFilterData) => void {
  const setQueryOptions = useContext(SetQueryOptionsContext);

  return useCallback(
    (filter: AdvancedFilterData) => {
      setQueryOptions((prev) => ({
        ...prev,
        filterGroup: {
          ...prev.filterGroup,
          clauses: [...prev.filterGroup.clauses, filter],
        },
      }));
    },
    [setQueryOptions],
  );
}

export function useRemoveFilter(): (filterName: string) => void {
  const setQueryOptions = useContext(SetQueryOptionsContext);

  return useCallback(
    (filterName: string) => {
      setQueryOptions((prev) => {
        const newClauses = prev.filterGroup.clauses.filter((clause) => {
          if (!("name" in clause)) {
            return true;
          }
          return clause.name !== filterName;
        });
        return {
          ...prev,
          filterGroup: { ...prev.filterGroup, clauses: newClauses },
        };
      });
    },
    [setQueryOptions],
  );
}

const UNIQUE_FILTER_NAMES = new Set<string>([SupportTicketFilterNames.STATUS]);

export function useAdvancedFilters(): {
  filters: AdvancedFilterData[];
  setFilters: Dispatch<SetStateAction<AdvancedFilterData[]>>;
} {
  const { queryOptions, setQueryOptions } = useQueryOptionsState();

  const filters = useMemo(() => {
    const leafFilters = getSupportTicketLeafFilters(queryOptions.filterGroup);
    return leafFilters.filter(
      (f) => !UNIQUE_FILTER_NAMES.has(f.name) && !isSpamTagExclusionFilter(f),
    );
  }, [queryOptions.filterGroup]);

  const setFilters: Dispatch<SetStateAction<AdvancedFilterData[]>> =
    useCallback(
      (action) => {
        setQueryOptions((prev) => {
          const allLeafFilters = getSupportTicketLeafFilters(prev.filterGroup);
          const uniqueFilters = allLeafFilters.filter((f) =>
            UNIQUE_FILTER_NAMES.has(f.name),
          );
          const implicitFilters = allLeafFilters.filter((f) =>
            isSpamTagExclusionFilter(f),
          );
          const currentAdvancedFilters = allLeafFilters.filter(
            (f) =>
              !UNIQUE_FILTER_NAMES.has(f.name) && !isSpamTagExclusionFilter(f),
          );
          const newAdvancedFilters =
            typeof action === "function"
              ? action(currentAdvancedFilters)
              : action;
          return {
            ...prev,
            filterGroup: {
              operator: FilterOperator.AND,
              clauses: [
                ...uniqueFilters,
                ...newAdvancedFilters,
                ...implicitFilters,
              ],
            },
          };
        });
      },
      [setQueryOptions],
    );

  return { filters, setFilters };
}

export function useFilterGroup(): FilterGroup {
  const queryOptions = useContext(QueryOptionsContext);
  return queryOptions.filterGroup;
}

export function useSetFilterGroup(): (filterGroup: FilterGroup) => void {
  const setQueryOptions = useContext(SetQueryOptionsContext);

  return useCallback(
    (filterGroup: FilterGroup) => {
      setQueryOptions((prev) => ({ ...prev, filterGroup }));
    },
    [setQueryOptions],
  );
}
